import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Pause, Play, Square } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import {
  useActiveTimers,
  usePauseTimer,
  useResumeTimer,
  useStopTimer,
  useTimerNow,
} from '../../hooks/useTaskTimers';
import { elapsedSeconds, formatElapsed, formatStartedAt } from '../../lib/timerClock';
import type { ActiveTimer } from '../../lib/timersApi';

/**
 * The global timer strip, pinned above the page content on every route.
 *
 * Every running timer gets its own compact row: working on four tasks at once
 * means seeing all four, each clickable and stoppable without a detour. With
 * several timers the rows flow into a grid — two per line from `md`, three from
 * `xl` — so six timers cost two lines on a wide screen instead of six. A single
 * timer always takes the full width. Renders nothing when no timer is running.
 */

function taskHref(timer: ActiveTimer): string {
  return `/tasks/${timer.taskIdentifier ?? timer.taskId}`;
}

/**
 * The bar is deliberately not the app's grey: a strip you cannot notice defeats
 * the point of a timer you might leave running for hours. Green while it runs,
 * amber while it is paused — never the red already used for errors and overrun.
 */
const RUNNING = {
  bar: 'border-green-500/40 bg-green-500/10',
  clock: 'text-green-600',
  dot: 'bg-green-500',
  button: 'text-green-600 hover:bg-green-500/15 hover:text-green-700',
} as const;

const PAUSED = {
  bar: 'border-amber-500/40 bg-amber-500/10',
  clock: 'text-amber-600',
  dot: 'bg-amber-500',
  button: 'text-amber-600 hover:bg-amber-500/15 hover:text-amber-700',
} as const;

function palette(timer: ActiveTimer) {
  return timer.runningSince !== null ? RUNNING : PAUSED;
}

/** Pulses only while running, so "paused" reads as paused at a glance. */
function StatusDot({ timer }: { timer: ActiveTimer }) {
  const running = timer.runningSince !== null;
  return (
    <span
      className={`h-2 w-2 shrink-0 rounded-full ${palette(timer).dot} ${
        running ? 'animate-pulse' : ''
      }`}
    />
  );
}

function TimerControls({ timer }: { timer: ActiveTimer }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const pause = usePauseTimer();
  const resume = useResumeTimer();
  const stop = useStopTimer();
  const running = timer.runningSince !== null;
  const busy = pause.isPending || resume.isPending || stop.isPending;

  const btn = `inline-flex items-center gap-1 rounded px-1.5 py-1 text-[12px] disabled:opacity-50 ${
    palette(timer).button
  }`;

  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <button
        type="button"
        disabled={busy}
        onClick={() => (running ? pause.mutate(timer.taskId) : resume.mutate(timer.taskId))}
        title={running ? t('timer.pause') : t('timer.resume')}
        className={btn}
      >
        {running ? <Pause size={13} /> : <Play size={13} />}
        <span>{running ? t('timer.pause') : t('timer.resume')}</span>
      </button>
      {/* Stopping lands you on the task: the session just became a worklog entry
          and the note is auto-generated, so the next thing you want is to check
          or correct it. Only on success — a failed stop must not move you away
          from wherever you were. */}
      <button
        type="button"
        disabled={busy}
        onClick={() => stop.mutate(timer.taskId, { onSuccess: () => navigate(taskHref(timer)) })}
        title={t('timer.stop')}
        className={btn}
      >
        <Square size={13} />
        <span>{t('timer.stop')}</span>
      </button>
    </div>
  );
}

function ElapsedClock({ timer, nowMs }: { timer: ActiveTimer; nowMs: number }) {
  return (
    <span
      className={`shrink-0 font-mono text-[16px] font-semibold tabular-nums ${palette(timer).clock}`}
    >
      {formatElapsed(elapsedSeconds(timer, nowMs))}
    </span>
  );
}

/**
 * One row per timer. Coloured per timer rather than per bar, so a paused one
 * among three running ones is obvious without reading anything.
 */
function TimerRow({ timer, nowMs }: { timer: ActiveTimer; nowMs: number }) {
  const { t } = useI18n();
  return (
    <div
      className={`flex min-w-0 items-center gap-2 border-b px-3 py-1.5 md:border-r ${palette(timer).bar}`}
    >
      <StatusDot timer={timer} />
      <Link
        to={taskHref(timer)}
        className="min-w-0 flex-1 truncate text-[13px] text-gray-800 hover:underline"
      >
        {timer.taskIdentifier && (
          <span className="mr-1.5 font-mono text-[11px] opacity-60">{timer.taskIdentifier}</span>
        )}
        {timer.taskTitle ?? t('timer.untitledTask')}
      </Link>
      <span className="hidden shrink-0 text-[11px] text-gray-800 opacity-50 lg:inline">
        {t('timer.startedAt', { time: formatStartedAt(timer.startedAt) })}
      </span>
      <ElapsedClock timer={timer} nowMs={nowMs} />
      <TimerControls timer={timer} />
    </div>
  );
}

/**
 * Columns grow with the number of timers, never past what the viewport can
 * hold: 1 → full width, 2 → two from `md`, 3+ → three from `xl`.
 */
function gridColumns(count: number): string {
  if (count <= 1) return 'grid-cols-1';
  if (count === 2) return 'grid-cols-1 md:grid-cols-2';
  return 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3';
}

export function TimerBar() {
  const { data, refetch } = useActiveTimers();
  const nowMs = useTimerNow(data?.serverNow);

  // Coming back to the tab: the interval was throttled while hidden, so pull
  // fresh state instead of trusting a possibly-stopped timer.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') void refetch();
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refetch]);

  const timers = data?.timers ?? [];
  if (timers.length === 0) return null;

  // Server order is startedAt asc, so rows keep their place instead of jumping
  // around when one is paused or stopped.
  return (
    <div className={`grid shrink-0 ${gridColumns(timers.length)}`}>
      {timers.map((timer) => (
        <TimerRow key={timer.id} timer={timer} nowMs={nowMs} />
      ))}
    </div>
  );
}
